import dayjs from "dayjs";

import { apiGetAtmCalls } from "api/apiOptions";
import { blackScholesCall } from "components/OptionSimulator/utils/blackScholes";
import {
  easternMarketCloseOnCalendarDate,
  getTradingDaysUntilExpiration,
  getDaysToExpiration,
  daysToYears,
} from "components/OptionSimulator/utils/tradingDays";

const RISK_FREE_RATE = 0.045;

const profitPercent = (entry, exit) =>
  entry > 0 ? ((exit - entry) / entry) * 100 : null;

// Entry is what you'd actually pay (ask); falls back to last when there's no
// ask (e.g. after hours).
const pickEntryPrice = ({ ask, last }) => {
  if (ask > 0) return ask;
  return last > 0 ? last : null;
};

/**
 * Prices a live ATM call quote with the same Black-Scholes + trading-day
 * conventions as the Options Simulator.
 *
 * Profit range assumes the underlying reaches targetGainPercent:
 *   - bestCase: target hit at the close of trading day 1 (BS value, same IV)
 *   - worstCase: target only reached at expiration (intrinsic value)
 *
 * No range at all (profitUnavailableReason set) without IV or an entry price:
 * missing IV usually means IBKR has no live quote for the contract, so even
 * the IV-free worst case would rest on a stale/unreliable entry price.
 */
const computeAtmOptionSnapshot = (quote, params = {}) => {
  const targetGainPercent = Number(params.targetGainPercent) || 0;
  const { expiration, underlyingPrice, strike, iv } = quote;

  const expirationClose = easternMarketCloseOnCalendarDate(
    dayjs(expiration, "YYYYMMDD"),
  );
  const asOf = quote.time ? dayjs(quote.time) : dayjs();
  const tradingDays = getTradingDaysUntilExpiration(expirationClose, asOf);
  const entryPrice = pickEntryPrice(quote);
  const targetPrice = underlyingPrice * (1 + targetGainPercent / 100);

  let profitUnavailableReason = null;
  if (!(iv > 0)) profitUnavailableReason = "No IV from IBKR";
  else if (!entryPrice) profitUnavailableReason = "No ask/last price from IBKR";

  let bestCase = null;
  if (!profitUnavailableReason && tradingDays.length) {
    const T = daysToYears(getDaysToExpiration(tradingDays[0], expirationClose));
    const exit = blackScholesCall(targetPrice, strike, T, RISK_FREE_RATE, iv);
    bestCase = {
      exitPrice: exit,
      profitPercent: profitPercent(entryPrice, exit),
    };
  }

  let worstCase = null;
  if (!profitUnavailableReason) {
    const exit = Math.max(targetPrice - strike, 0);
    worstCase = {
      exitPrice: exit,
      profitPercent: profitPercent(entryPrice, exit),
    };
  }

  return {
    ...quote,
    tradingDaysToExpiration: tradingDays.length,
    entryPrice,
    targetPrice,
    bestCase,
    worstCase,
    profitUnavailableReason,
  };
};

/**
 * Live ATM call snapshots for option strategies in a single IBKR batch request.
 * The IBKR service picks the nearest expiration at least minDays trading bars
 * out (at least 1, so a "day 1" exit exists); maxDays is ignored for now.
 *
 * @returns {Promise<Object<string, {data?: object, error?: string}>>} keyed by item _id
 */
export const fetchAtmOptionSnapshots = async (items) => {
  const { results } = await apiGetAtmCalls(
    items.map((item) => ({
      symbol: item.symbol,
      minDays: Math.max(Number(item.params?.minDays) || 0, 1),
    })),
  );

  return Object.fromEntries(
    items.map((item, idx) => {
      const quote = results[idx];
      if (!quote || quote.error) {
        return [item._id, { error: quote?.error || "No data" }];
      }
      return [item._id, { data: computeAtmOptionSnapshot(quote, item.params) }];
    }),
  );
};
